
import React, { useState, useEffect } from "react";
import LayoutHeader from "../../Common/Layout/Header";
import AccountTabs from "../AllTabs/AccountTabs"; // Import AccountTabs
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { FaCalendarAlt } from "react-icons/fa";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  Legend,
  ResponsiveContainer,
} from "recharts";
import apiService from "../../Common/Configurations/APIService";
import config from "../../Common/Configurations/APIConfig";
import { useAuth } from "../../Common/Configurations/AuthContext";
import moment from "moment";
import Login from "../../Authentication/Login";
import { FiTrendingUp, FiTrendingDown, FiFileText } from "react-icons/fi";
import "./AccountDashboard.css";
import { useNavigate } from "react-router-dom";

const AccountDashboard = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [monthlyData, setMonthlyData] = useState([]);
  const [topExpenses, setTopExpenses] = useState([]);
  const [topIncomes, setTopIncomes] = useState([]);
  const [invoiceSummary, setInvoiceSummary] = useState({
    totalInvoices: 0,
    paidInvoices: 0,
    unpaidInvoices: 0,
  });
  const [chequeSummary, setChequeSummary] = useState({
    totalIssued: 0,
    totalBounced: 0,
    totalPending: 0,
    totalCleared: 0,
  });
  const [profitLossReport, setProfitLossReport] = useState({
    totalIncome: 0,
    totalExpense: 0,
    netProfit: 0,
  });
  const [fromDate, setFromDate] = useState(moment().startOf("month").toDate());
  const [toDate, setToDate] = useState(moment().endOf("month").toDate());
  const [shouldFetchData, setShouldFetchData] = useState(false);
  const [clearCounter, setClearCounter] = useState(0);
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState("all");

  // Chart colors
  const COLORS = ["#0088FE", "#FF8042", "#00C49F", "#FFBB28", "#FF6384"];

  // Helper function to format currency
  const formatCurrency = (amount) => {
    const currency = user?.currency || "INR"; // Fallback to INR if currency is not available
    return `${currency} ${amount.toLocaleString()}`;
  };

  // Get branches for the dropdown
  const GetBranches = async () => {
    try {
      const res = await apiService.getAll(
        `${config.GETBRANCHBYCOMPANY}/${user?.cid}`
      );
      setBranches(res.data || res);
    } catch (error) {
      console.error("Error fetching branches:", error);
    }
  };

  const GetProfitLossReport = async () => {
    try {
      const params = new URLSearchParams();
      if (selectedBranch && selectedBranch !== "all") {
        params.append("branchId", selectedBranch);
      }
      params.append("fromDate", moment(fromDate).format("YYYY-MM-DD"));
      params.append("toDate", moment(toDate).format("YYYY-MM-DD"));

      const res = await apiService.getAll(
        `${config.PROFITLOSSREPORT}/${user.id}?${params.toString()}`
      );

      if (res && res.length > 0) {
        const totals = res.reduce(
          (acc, curr) => ({
            totalIncome: acc.totalIncome + (curr.totalIncome || 0),
            totalExpense: acc.totalExpense + (curr.totalExpense || 0),
            netProfit: acc.netProfit + (curr.netProfit || 0),
          }),
          { totalIncome: 0, totalExpense: 0, netProfit: 0 }
        );

        setProfitLossReport(totals);

        // Prepare monthly data from profit loss report
        prepareMonthlyData(res);
      }
    } catch (err) {
      console.error("Error fetching profit loss report:", err);
    }
  };

  const GetInvoiceSummary = async () => {
    try {
      const params = new URLSearchParams();
      if (selectedBranch && selectedBranch !== "all") {
        params.append("branchId", selectedBranch);
      }
      params.append("fromDate", moment(fromDate).format("YYYY-MM-DD"));
      params.append("toDate", moment(toDate).format("YYYY-MM-DD"));

      const res = await apiService.getAll(
        `${config.INVOICESUMMARY}/${user.id}?${params.toString()}`
      );
      if (res) setInvoiceSummary(res);
    } catch (err) {
      console.error("Error fetching invoice summary:", err);
    }
  };

  const GetChequeSummary = async () => {
    try {
      const params = new URLSearchParams();
      if (selectedBranch && selectedBranch !== "all") {
        params.append("branchId", selectedBranch);
      }
      params.append("fromDate", moment(fromDate).format("YYYY-MM-DD"));
      params.append("toDate", moment(toDate).format("YYYY-MM-DD"));

      const res = await apiService.getAll(
        `${config.CHEQUESUMMARY}/${user.id}?${params.toString()}`
      );
      if (res) setChequeSummary(res);
    } catch (err) {
      console.error("Error fetching cheque summary:", err);
    }
  };

  // Data processing functions
  const prepareMonthlyData = (profitLossData) => {
    const monthlySummary = {};

    profitLossData.forEach((item) => {
      const dateField = item.transactionDate || item.invoiceDate || item.date;
      if (!dateField) return;

      const month = moment(dateField).format("MMM YYYY");
      if (!monthlySummary[month]) {
        monthlySummary[month] = { month, income: 0, expense: 0 };
      }

      monthlySummary[month].income += item.totalIncome || 0;
      monthlySummary[month].expense += item.totalExpense || 0;
    });

    const monthlyArray = Object.values(monthlySummary);
    console.log("Monthly Data from Profit Loss Report:", monthlyArray);
    setMonthlyData(monthlyArray);

    prepareTopExpensesData(profitLossData);
    prepareTopIncomesData(profitLossData);
  };

  const prepareTopExpensesData = (profitLossData) => {
    const expenseByCategory = {};

    profitLossData.forEach((item) => {
      if (item.totalExpense && item.totalExpense > 0) {
        const category =
          item.category ||
          item.accountName ||
          item.description ||
          "Other Expenses";
        expenseByCategory[category] =
          (expenseByCategory[category] || 0) + item.totalExpense;
      }
    });

    const topExpenses = Object.entries(expenseByCategory)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 5);

    console.log("Top Expenses from Profit Loss:", topExpenses);
    setTopExpenses(topExpenses);
  };

  const prepareTopIncomesData = (profitLossData) => {
    const incomeByCategory = {};

    profitLossData.forEach((item) => {
      if (item.totalIncome && item.totalIncome > 0) {
        const category =
          item.category ||
          item.accountName ||
          item.description ||
          "Other Income";
        incomeByCategory[category] =
          (incomeByCategory[category] || 0) + item.totalIncome;
      }
    });

    const topIncomes = Object.entries(incomeByCategory)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 5);

    console.log("Top Incomes from Profit Loss:", topIncomes);
    setTopIncomes(topIncomes);
  };

  // Custom tooltip formatter for charts
  const chartTooltipFormatter = (value) => {
    return [formatCurrency(value), "Amount"];
  };

  // Event handlers
  const handleSearch = () => {
    setShouldFetchData(true);
  };

  const handleClear = () => {
    setClearCounter((prev) => prev + 1);
    const currentMonthStart = moment().startOf("month").toDate();
    const currentMonthEnd = moment().endOf("month").toDate();
    setFromDate(currentMonthStart);
    setToDate(currentMonthEnd);
    setSelectedBranch("all");
    setShouldFetchData(true);
  };

  // Effects
  useEffect(() => {
    if (user && !loading && shouldFetchData) {
      setProfitLossReport({
        totalIncome: 0,
        totalExpense: 0,
        netProfit: 0,
      });
      setInvoiceSummary({
        totalInvoices: 0,
        paidInvoices: 0,
        unpaidInvoices: 0,
      });
      setChequeSummary({
        totalIssued: 0,
        totalBounced: 0,
        totalPending: 0,
        totalCleared: 0,
      });
      setMonthlyData([]);
      setTopExpenses([]);
      setTopIncomes([]);

      Promise.all([
        GetProfitLossReport(),
        GetInvoiceSummary(),
        GetChequeSummary(),
      ]).finally(() => {
        setShouldFetchData(false);
      });
    }
  }, [user, loading, shouldFetchData, clearCounter, selectedBranch]);

  // Initial data load
  useEffect(() => {
    if (user && !loading) {
      GetBranches();
      setShouldFetchData(true);
    }
  }, [user, loading]);

  if (loading) return <p>Loading...</p>;
  if (!user) {
    navigate("/");
  }

  return (
    <div id="layout-wrapper" className="dashboard-wrapper">
      <LayoutHeader />
      <div className="main-content">
        <div className="page-content">
          <div className="container-fluid">
            {/* Add AccountTabs here - similar to SaleInvoice */}
            <div className="row">
              <div className="col-xl-12">
                <div className="card">
                 
                </div>
              </div>
            </div>

            {/* Header with Date Filters in Card */}
            <div className="account-filter-card">
              <div className="dashboard-header">
                <h1>Accounts</h1>
                <div className="date-filter-container">
                  <div className="account-filter-group">
                    <label className="account-filter-label">Branch</label>
                    <select
                      className="account-branch-select"
                      value={selectedBranch}
                      onChange={(e) => setSelectedBranch(e.target.value)}
                    >
                      <option value="all">All Branches</option>
                      {branches.map((branch) => (
                        <option key={branch.branchId} value={branch.branchId}>
                          {branch.branchName}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="account-filter-group">
                    <label className="account-filter-label">From Date</label>
                    <div className="account-date-picker-wrapper">
                      <DatePicker
                        selected={fromDate}
                        onChange={(date) => setFromDate(date)}
                        dateFormat="dd-MMM-yyyy"
                        className="account-custom-datepicker"
                        placeholderText="Select from date"
                      />
                      <FaCalendarAlt className="account-date-icon account-right-icon" />
                    </div>
                  </div>

                  <div className="account-filter-group">
                    <label className="account-filter-label">To Date</label>
                    <div className="account-date-picker-wrapper">
                      <DatePicker
                        selected={toDate}
                        onChange={(date) => setToDate(date)}
                        minDate={fromDate}
                        dateFormat="dd-MMM-yyyy"
                        className="account-custom-datepicker"
                        placeholderText="Select to date"
                      />
                      <FaCalendarAlt className="account-date-icon account-right-icon" />
                    </div>
                  </div>

                  <div className="account-filter-group account-button-group">
                    <button
                      className="account-action-btn account-search-btn"
                      onClick={handleSearch}
                    >
                      Search
                    </button>
                    <button
                      className="account-action-btn account-reset-btn"
                      onClick={handleClear}
                    >
                      Reset
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Summary Cards */}
            <div className="account-summary-cards-grid">
              <div className="account-summary-card account-income-card">
                <div className="account-card-icon">
                  <FiTrendingUp />
                </div>
                <div className="account-card-content">
                  <h3>Total Income</h3>
                  <h2>{formatCurrency(profitLossReport.totalIncome)}</h2>
                </div>
              </div>

              <div className="account-summary-card account-expense-card">
                <div className="account-card-icon">
                  <FiTrendingDown />
                </div>
                <div className="account-card-content">
                  <h3>Total Expenses</h3>
                  <h2>{formatCurrency(profitLossReport.totalExpense)}</h2>
                </div>
              </div>

              <div
                className={`account-summary-card ${
                  profitLossReport.netProfit >= 0
                    ? "account-profit-card"
                    : "account-loss-card"
                }`}
              >
                <div className="account-card-icon">
                  {profitLossReport.netProfit >= 0 ? (
                    <FiTrendingUp />
                  ) : (
                    <FiTrendingDown />
                  )}
                </div>
                <div className="account-card-content">
                  <h3>
                    {profitLossReport.netProfit >= 0
                      ? "Net Profit"
                      : "Net Loss"}
                  </h3>
                  <h2>
                    {profitLossReport.netProfit >= 0 ? "" : "-"}
                    {formatCurrency(Math.abs(profitLossReport.netProfit))}
                  </h2>
                </div>
              </div>

              <div className="account-summary-card account-invoice-card">
                <div className="account-card-icon">
                  <FiFileText />
                </div>
                <div className="account-card-content">
                  <h3>Invoices Summary</h3>
                  <div className="account-invoice-stats">
                    <div className="account-stat-item">
                      <span>Total</span>
                      <strong>{invoiceSummary.totalInvoices}</strong>
                    </div>
                    <div className="account-stat-item account-paid">
                      <span>Paid</span>
                      <strong>{invoiceSummary.paidInvoices}</strong>
                    </div>
                    <div className="account-stat-item account-unpaid">
                      <span>Unpaid</span>
                      <strong>{invoiceSummary.unpaidInvoices}</strong>
                    </div>
                  </div>
                </div>
              </div>

              <div className="account-summary-card account-cheque-card">
                <div className="account-card-icon">
                  <FiFileText />
                </div>
                <div className="account-card-content">
                  <h3>Cheque Summary</h3>
                  <div className="account-cheque-stats">
                    <div className="account-stat-item">
                      <span>Issued</span>
                      <strong>{chequeSummary.totalIssued}</strong>
                    </div>
                    <div className="account-stat-item account-cleared">
                      <span>Cleared</span>
                      <strong>{chequeSummary.totalCleared}</strong>
                    </div>
                    <div className="account-stat-item account-pending">
                      <span>Pending</span>
                      <strong>{chequeSummary.totalPending}</strong>
                    </div>
                    <div className="account-stat-item account-bounced">
                      <span>Bounced</span>
                      <strong>{chequeSummary.totalBounced}</strong>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Charts Section */}
            <div className="account-charts-grid">
              {/* Monthly Income vs Expenses */}
              <div className="account-chart-container">
                <div className="account-chart-header">
                  <h3>Monthly Income vs Expenses</h3>
                </div>
                <div className="account-chart-wrapper">
                  <ResponsiveContainer width="100%" height={350}>
                    <BarChart data={monthlyData}>
                      <XAxis
                        dataKey="month"
                        angle={-45}
                        textAnchor="end"
                        height={70}
                      />
                      <YAxis />
                      <Tooltip formatter={chartTooltipFormatter} />
                      <Legend />
                      <Bar
                        dataKey="income"
                        name="Income"
                        fill="#4ade80"
                        radius={[4, 4, 0, 0]}
                      />
                      <Bar
                        dataKey="expense"
                        name="Expense"
                        fill="#f87171"
                        radius={[4, 4, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Top 5 Incomes */}
              <div className="account-chart-container">
                <div className="account-chart-header">
                  <h3>Top 5 Income</h3>
                </div>
                <div className="account-chart-wrapper">
                  <div className="account-pie-chart-container">
                    <div className="account-pie-legend">
                      {topIncomes.map((entry, index) => (
                        <div key={entry.name} className="account-legend-item">
                          <div
                            className="account-legend-color"
                            style={{
                              backgroundColor: COLORS[index % COLORS.length],
                            }}
                          ></div>
                          <span className="account-legend-text">
                            {entry.name}
                          </span>
                        </div>
                      ))}
                    </div>
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={topIncomes}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          outerRadius={100}
                          innerRadius={60}
                          paddingAngle={2}
                        >
                          {topIncomes.map((entry, index) => (
                            <Cell
                              key={`cell-${index}`}
                              fill={COLORS[index % COLORS.length]}
                            />
                          ))}
                        </Pie>
                        <Tooltip formatter={chartTooltipFormatter} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>

              {/* Top 5 Expenses */}
              <div className="account-chart-container">
                <div className="account-chart-header">
                  <h3>Top 5 Expense</h3>
                </div>
                <div className="account-chart-wrapper">
                  <div className="account-pie-chart-container">
                    <div className="account-pie-legend">
                      {topExpenses.map((entry, index) => (
                        <div key={entry.name} className="account-legend-item">
                          <div
                            className="account-legend-color"
                            style={{
                              backgroundColor: COLORS[index % COLORS.length],
                            }}
                          ></div>
                          <span className="account-legend-text">
                            {entry.name}
                          </span>
                        </div>
                      ))}
                    </div>
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={topExpenses}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          outerRadius={100}
                          innerRadius={60}
                          paddingAngle={2}
                        >
                          {topExpenses.map((entry, index) => (
                            <Cell
                              key={`cell-${index}`}
                              fill={COLORS[index % COLORS.length]}
                            />
                          ))}
                        </Pie>
                        <Tooltip formatter={chartTooltipFormatter} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountDashboard;



